/*
 * File: ContentContainer.tsx
 * Project: meki
 * File Created: Wednesday, 28th October 2020 3:47:54 pm
 * Author: Esperanza Horn (esperanza@inventures.cl)
 * -----
 * Last Modified: Friday, 24th November 2023 5:17:04 pm
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2020 - 2020 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */

import {
  ForwardRefExoticComponent,
  MutableRefObject,
  RefAttributes,
  forwardRef,
} from 'react';
import { styled } from '@mui/system';

interface ContentContainerProps {
  children: React.ReactNode;
  isEmbedded?: boolean;
  marginTopStyle?: string;
  bottomNavigation?: boolean;
  background?: string;
  className?: string;
  id?: string;
  hideBottom?: boolean;
  hasBorder?: boolean;
  desktopHeight?: string;
}

interface ContentDivProps {
  marginTopStyle: string;
  background: string;
  bottomNavigation: boolean;
  hasBorder?: boolean;
  desktopHeight?: string;
}
const ContentDiv = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'desktopHeight' &&
    prop !== 'bottomNavigation' &&
    prop !== 'background' &&
    prop !== 'marginTopStyle' &&
    prop !== 'hasBorder',
})<ContentDivProps>(
  ({
    bottomNavigation,
    background = 'white',
    marginTopStyle = 0,
    hasBorder = true,
    desktopHeight = null,
  }) => ({
    paddingBottom: bottomNavigation ? 52 : '',
    marginTop: marginTopStyle,
    paddingTop: 0,
    overflowY: 'hidden',
    position: 'relative',
    background,
    borderRadius: hasBorder ? 12 : 0,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    msOverflowStyle: 'none' /* IE and Edge */,
    scrollbarWidth: 'none' /* Firefox */,
    height: desktopHeight ? desktopHeight : 'calc(90dvh)',
    '@media (max-width: 900px)': {
      minHeight: '100dvh',
      height: 'unset',
      overflowY: 'hidden',
    },
  }),
);

export function ContentContainerFunction(
  props: ContentContainerProps,
  ref: MutableRefObject<HTMLDivElement>,
) {
  /**
   * Custom Meki container that includes top padding for the
   * Meki Header
   *
   * @param BottomActionTabProps - left and right react components.
   * @returns React Component
   *
   * @beta
   */
  return (
    <ContentDiv
      desktopHeight={props.desktopHeight}
      bottomNavigation={props.bottomNavigation}
      marginTopStyle={props.marginTopStyle}
      background={props.background}
      className={props.className}
      id={props.id}
      ref={ref}
      hasBorder={props.hasBorder}
    >
      {props.children}
    </ContentDiv>
  );
}

type IContentContainer = ForwardRefExoticComponent<
  ContentContainerProps & RefAttributes<unknown>
>;

export const ContentContainer: IContentContainer = forwardRef(
  ContentContainerFunction,
);
